/**
 * Hybrid Storage Hook
 *
 * Abstraction layer that automatically switches between:
 * - LOCAL mode: localStorage (default, no auth required)
 * - CLOUD mode: Supabase (optional, when user is authenticated)
 *
 * Features:
 * - Zero-config: detects mode automatically
 * - Backward compatible: works exactly like current localStorage
 * - Optional cloud sync: user can enable/disable
 * - localStorage always used as cache (even in cloud mode)
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/client';
import { StorageService } from '../services/storage';
import type { Session } from '@supabase/supabase-js';

// Storage key used by Supabase client (must match secureTokenStorage)
const STORAGE_PREFIX = 'wealthhub-secure-';
const SUPABASE_STORAGE_KEY = 'wealthhub-auth';

/**
 * Fast session retrieval directly from localStorage
 * This avoids waiting for Supabase client initialization/network calls
 */
/**
 * Check if a JWT token is expired
 */
function isTokenExpired(accessToken: string): boolean {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    if (!exp) return false;

    // Add 30 second buffer to account for clock skew
    return Date.now() >= (exp * 1000) - 30000;
  } catch {
    return true;
  }
}

/**
 * Get session from localStorage with expiry info
 */
function getSessionFromStorage(): { session: Session | null; isExpired: boolean } {
  try {
    // Read from the correct prefixed key
    const stored = localStorage.getItem(STORAGE_PREFIX + SUPABASE_STORAGE_KEY);
    if (!stored) {
      return { session: null, isExpired: false };
    }

    // Decode base64 (matching secureTokenStorage encoding)
    let decoded: string;
    try {
      decoded = atob(stored);
      // Verify it's valid JSON after decoding
      JSON.parse(decoded);
    } catch {
      // Not valid base64 or the decoded result isn't JSON
      // Try treating it as raw JSON
      try {
        JSON.parse(stored);
        decoded = stored;
      } catch {
        return { session: null, isExpired: false };
      }
    }

    const parsed = JSON.parse(decoded);

    // Handle potential wrapped session format (e.g., { currentSession: {...} })
    const session = parsed?.currentSession || parsed;

    if (!session?.access_token) {
      return { session: null, isExpired: false };
    }

    // Check if token is expired
    const isExpired = isTokenExpired(session.access_token);

    // If expired and no refresh token, don't use this session
    if (isExpired && !session.refresh_token) {
      return { session: null, isExpired: true };
    }

    // Return session with expiry status
    return { session: session as Session, isExpired };
  } catch {
    return { session: null, isExpired: false };
  }
}

export type StorageMode = 'local' | 'cloud';

export interface HybridStorageState {
  mode: StorageMode;
  isAuthenticated: boolean;
  cloudEnabled: boolean;
  userId: string | null;
  isInitialized: boolean; // True once initial auth check is complete
}

/**
 * Hook that provides hybrid storage capabilities
 */
export function useHybridStorage() {
  const [state, setState] = useState<HybridStorageState>({
    mode: 'local',
    isAuthenticated: false,
    cloudEnabled: false,
    userId: null,
    isInitialized: false, // Will be true after first auth check
  });

  /**
   * Update state based on session
   * Uses session directly instead of calling getSession() again
   */
  const updateStateFromSession = useCallback((session: Session | null, _source: string) => {
    const isAuthenticated = !!session?.user;
    const userId = session?.user?.id || null;
    const cloudEnabled = isAuthenticated;

    setState({
      mode: isAuthenticated ? 'cloud' : 'local',
      isAuthenticated,
      cloudEnabled,
      userId,
      isInitialized: true, // Mark as initialized after first auth check
    });
  }, []);

  // Single auth listener - uses session from callback directly
  useEffect(() => {
    let isCleanedUp = false;

    // FAST PATH: Try to get session from localStorage first (instant)
    const { session: storedSession, isExpired: isStoredSessionExpired } = getSessionFromStorage();

    // Only use fast path if token is NOT expired
    // If token is expired, we need to wait for Supabase to refresh it
    if (storedSession && !isStoredSessionExpired) {
      console.log('[useHybridStorage] Fast path: using valid stored session');
      updateStateFromSession(storedSession, 'localStorage (fast path)');
    } else if (storedSession && isStoredSessionExpired) {
      console.log('[useHybridStorage] Stored session expired, waiting for Supabase refresh...');
      // Don't set isInitialized yet - wait for slow path to refresh the token
    }

    // SLOW PATH: Get session from Supabase (handles token refresh)
    // This will update the state with a fresh/refreshed session
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<{ data: { session: null }; error: Error }>((resolve) => {
      setTimeout(() => resolve({
        data: { session: null },
        error: new Error('Session fetch timeout')
      }), 5000); // Increased to 5s to allow token refresh
    });

    Promise.race([sessionPromise, timeoutPromise]).then(({ data: { session }, error }) => {
      if (isCleanedUp) return;

      if (error) {
        console.warn('[useHybridStorage] Supabase session error:', error.message);
        // If we have a non-expired stored session, use it
        if (storedSession && !isStoredSessionExpired) {
          setState((prev) => ({ ...prev, isInitialized: true }));
        } else {
          // No valid session available
          updateStateFromSession(null, 'Fallback (session error)');
        }
      } else if (session) {
        // Fresh/refreshed session from Supabase - always use this
        console.log('[useHybridStorage] Using fresh session from Supabase');
        updateStateFromSession(session, 'Supabase (fresh/refreshed)');
      } else {
        // No session from Supabase - user is not authenticated
        console.log('[useHybridStorage] No session from Supabase');
        updateStateFromSession(null, 'No session');
      }
    }).catch((err) => {
      console.error('[useHybridStorage] Session fetch error:', err);
      // Handle any unexpected errors in the Promise chain
      if (!isCleanedUp) {
        // If we have a valid stored session, keep using it
        if (storedSession && !isStoredSessionExpired) {
          setState((prev) => ({ ...prev, isInitialized: true }));
        } else {
          updateStateFromSession(null, 'Error fallback');
        }
      }
    });

    // SAFETY TIMEOUT: Ensure isInitialized is set even if everything fails
    const safetyTimeout = setTimeout(() => {
      if (!isCleanedUp) {
        setState((prev) => {
          if (!prev.isInitialized) {
            console.warn('[useHybridStorage] Safety timeout reached, forcing isInitialized');
            return { ...prev, isInitialized: true };
          }
          return prev;
        });
      }
    }, 8000); // Increased to 8s for slow networks

    // Listen for auth changes - use session from callback directly
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (isCleanedUp) return;
      console.log('[useHybridStorage] Auth state change:', event);
      updateStateFromSession(session, `Auth event: ${event}`);
    });

    return () => {
      isCleanedUp = true;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [updateStateFromSession]);

  /**
   * Enable cloud sync (requires authentication)
   */
  const enableCloudSync = async () => {
    if (!state.isAuthenticated) {
      throw new Error('User must be authenticated to enable cloud sync');
    }

    StorageService.set('cloud_sync_enabled', true);

    setState((prev) => ({
      ...prev,
      mode: 'cloud',
      cloudEnabled: true,
    }));
  };

  /**
   * Disable cloud sync (revert to local mode)
   */
  const disableCloudSync = () => {
    StorageService.set('cloud_sync_enabled', false);

    setState((prev) => ({
      ...prev,
      mode: 'local',
      cloudEnabled: false,
    }));
  };

  /**
   * Get data (from localStorage or Supabase)
   */
  const get = async <T,>(key: string, defaultValue: T): Promise<T> => {
    const cachedValue = StorageService.get<T>(key);

    if (state.mode === 'local') {
      return cachedValue !== null ? cachedValue : defaultValue;
    }

    return cachedValue !== null ? cachedValue : defaultValue;
  };

  /**
   * Set data (to localStorage and optionally Supabase)
   */
  const set = async <T,>(key: string, value: T): Promise<void> => {
    StorageService.set(key, value);
  };

  /**
   * Remove data
   */
  const remove = async (key: string): Promise<void> => {
    StorageService.remove(key);
  };

  /**
   * Clear all data (dangerous!)
   */
  const clear = async (): Promise<void> => {
    if (state.mode === 'cloud') {
      const confirmed = confirm(
        'This will delete ALL your cloud data. Are you sure?'
      );
      if (!confirmed) return;
    }

    StorageService.clear();
  };

  return {
    // State
    ...state,

    // Actions
    enableCloudSync,
    disableCloudSync,

    // Storage operations
    get,
    set,
    remove,
    clear,

    // Utilities
    isCloudMode: state.mode === 'cloud',
    isLocalMode: state.mode === 'local',
  };
}
